import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { BookMarked, Loader2, RefreshCw, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

export default function ZoteroPanel({ userId, projectId, onSynced }) {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [libraryId, setLibraryId] = useState('');
  const [libraryType, setLibraryType] = useState('user');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await thesisApi.zoteroStatus(userId, projectId)); } catch (e) { console.error(e); }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!apiKey.trim() || !libraryId.trim()) { toast.error('Paste your Zotero API key and numeric library ID'); return; }
    setBusy(true);
    try {
      const res = await thesisApi.zoteroConnect(userId, projectId, {
        api_key: apiKey.trim(), library_type: libraryType, library_id: libraryId.trim(),
      });
      toast.success(`Connected — ${res.total_items} items in the library`);
      setApiKey('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const sync = async (full) => {
    setBusy(true);
    try {
      const res = await thesisApi.zoteroSync(userId, projectId, full);
      toast.success(`Imported ${res.imported} references · ${res.skipped_duplicates} already present`);
      await load();
      onSynced?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.error : (d || e.message));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await thesisApi.zoteroDisconnect(userId, projectId); toast.success('Zotero disconnected'); await load(); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="zotero-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <BookMarked className="w-5 h-5 text-[#1d4ed8]" /> Zotero library sync
          {status?.connected && <Badge className="text-[10px] bg-emerald-600">connected</Badge>}
        </CardTitle>
        <CardDescription>
          Pull your Zotero items straight into this project's reference list. Only metadata is read
          (never your files), and repeat syncs transfer just what changed.
          Mendeley users: export a .ris/.bib file and use “Import references” above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!status?.connected ? (
          <>
            <div className="flex flex-wrap gap-2">
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={libraryType} onChange={(e) => setLibraryType(e.target.value)}
                data-testid="zotero-library-type">
                <option value="user">My library</option>
                <option value="group">Group library</option>
              </select>
              <Input className="w-40" value={libraryId} placeholder="Numeric library ID"
                onChange={(e) => setLibraryId(e.target.value)} data-testid="zotero-library-id" />
              <Input className="min-w-[240px] flex-1" type="password" value={apiKey}
                placeholder="Zotero API key (read-only)"
                onChange={(e) => setApiKey(e.target.value)} data-testid="zotero-api-key" />
              <Button onClick={connect} disabled={busy} data-testid="zotero-connect-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Connect
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Create a read-only key at{' '}
              <a className="text-[#1d4ed8] underline" href="https://www.zotero.org/settings/keys/new"
                target="_blank" rel="noreferrer">zotero.org/settings/keys</a>
              {' '}— the same page shows your numeric user ID. Your key is stored encrypted.
            </p>
          </>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground" data-testid="zotero-status">
              {status.library_type === 'group' ? 'Group' : 'User'} library {status.library_id} ·{' '}
              {status.total_items} items · {status.imported} imported here
              {status.last_sync ? ` · last sync ${new Date(status.last_sync).toLocaleString()}` : ' · never synced'}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => sync(false)} disabled={busy} data-testid="zotero-sync-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync new & changed
              </Button>
              <Button size="sm" variant="outline" onClick={() => sync(true)} disabled={busy}
                data-testid="zotero-full-sync-btn">Full re-import</Button>
              <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy}
                data-testid="zotero-disconnect-btn">
                <Unlink className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
